import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { MapPin, RotateCw } from "lucide-react";
import { AccessDenied } from "@/components/AccessDenied";
import { useAuth } from "@/hooks/useAuth";

export const Route = createFileRoute("/admin/mapa-audit")({
  component: Page,
  head: () => ({
    meta: [
      { title: "Audit log mapy — Admin" },
      { name: "description", content: "Přehled přístupů na stránku /mapa." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

type Result = "allowed" | "denied" | "anonymous";
type Row = {
  id: string;
  created_at: string;
  user_id: string | null;
  email: string | null;
  path: string | null;
  lang: string | null;
  result: Result;
  user_agent: string | null;
};

const RESULT_VARIANT: Record<Result, "default" | "secondary" | "destructive" | "outline"> = {
  allowed: "secondary",
  denied: "destructive",
  anonymous: "outline",
};

const fmt = (s: string) => new Date(s).toLocaleString("cs-CZ");

function Page() {
  const { hasRole, loading } = useAuth();
  const [result, setResult] = useState<string>("all");
  const [email, setEmail] = useState("");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [limit, setLimit] = useState(200);

  const isAdmin = hasRole("admin");

  const filters = useMemo(
    () => ({ result, email: email.trim(), since, until, limit }),
    [result, email, since, until, limit],
  );

  const { data, isFetching, error, refetch } = useQuery({
    enabled: isAdmin,
    queryKey: ["admin", "mapa-access-log", filters],
    queryFn: async (): Promise<Row[]> => {
      let q = supabase
        .from("mapa_access_log")
        .select("id, created_at, user_id, email, path, lang, result, user_agent")
        .order("created_at", { ascending: false })
        .limit(filters.limit);
      if (filters.result !== "all") q = q.eq("result", filters.result);
      if (filters.email) q = q.ilike("email", `%${filters.email}%`);
      if (filters.since) q = q.gte("created_at", new Date(filters.since).toISOString());
      if (filters.until) q = q.lt("created_at", new Date(filters.until).toISOString());
      const { data, error } = await q;
      if (error) throw new Error(error.message);
      return (data ?? []) as Row[];
    },
  });

  const summary = useMemo(() => {
    const rows = data ?? [];
    return {
      total: rows.length,
      allowed: rows.filter((r) => r.result === "allowed").length,
      denied: rows.filter((r) => r.result === "denied").length,
      anonymous: rows.filter((r) => r.result === "anonymous").length,
    };
  }, [data]);

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Načítám…</div>;
  if (!isAdmin) {
    return (
      <AccessDenied
        requiredRole="admin"
        description="Audit log mapy vidí pouze administrátoři."
      />
    );
  }

  const reset = () => {
    setResult("all");
    setEmail("");
    setSince("");
    setUntil("");
    setLimit(200);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <MapPin className="h-5 w-5" />
        <h1 className="text-2xl font-semibold">Audit log mapy</h1>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Celkem</div><div className="text-2xl font-semibold tabular-nums">{summary.total}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Povoleno</div><div className="text-2xl font-semibold tabular-nums text-emerald-600">{summary.allowed}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Odepřeno</div><div className="text-2xl font-semibold tabular-nums text-destructive">{summary.denied}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Anonymní</div><div className="text-2xl font-semibold tabular-nums">{summary.anonymous}</div></CardContent></Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filtry</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          <div>
            <Label>Výsledek</Label>
            <Select value={result} onValueChange={setResult}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Všechny</SelectItem>
                <SelectItem value="allowed">Povoleno</SelectItem>
                <SelectItem value="denied">Odepřeno</SelectItem>
                <SelectItem value="anonymous">Anonymní</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="lg:col-span-2">
            <Label htmlFor="email">E-mail obsahuje</Label>
            <Input id="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="např. @firma.cz" />
          </div>
          <div>
            <Label htmlFor="since">Od</Label>
            <Input id="since" type="datetime-local" value={since} onChange={(e) => setSince(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="until">Do</Label>
            <Input id="until" type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="limit">Limit</Label>
            <Input
              id="limit"
              type="number"
              min={1}
              max={1000}
              value={limit}
              onChange={(e) => setLimit(Math.max(1, Math.min(1000, Number(e.target.value) || 200)))}
            />
          </div>
          <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-6">
            <Button onClick={() => refetch()} disabled={isFetching} className="gap-1.5">
              <RotateCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
              Načíst
            </Button>
            <Button variant="ghost" onClick={reset}>Vyčistit</Button>
            <div className="ml-auto text-xs text-muted-foreground">
              {data ? `${data.length} záznam(ů)` : ""}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {error ? (
            <div className="p-4 text-sm text-destructive">{(error as Error).message}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Čas</TableHead>
                  <TableHead>Výsledek</TableHead>
                  <TableHead>E-mail</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Cesta</TableHead>
                  <TableHead>Jazyk</TableHead>
                  <TableHead>User-Agent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data ?? []).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap text-xs">{fmt(r.created_at)}</TableCell>
                    <TableCell><Badge variant={RESULT_VARIANT[r.result]}>{r.result}</Badge></TableCell>
                    <TableCell className="text-xs">{r.email ?? "—"}</TableCell>
                    <TableCell className="font-mono text-[11px] text-muted-foreground">
                      {r.user_id ? r.user_id.slice(0, 8) + "…" : "—"}
                    </TableCell>
                    <TableCell className="max-w-[220px] truncate font-mono text-[11px]" title={r.path ?? ""}>{r.path ?? "—"}</TableCell>
                    <TableCell className="text-xs">{r.lang ?? "—"}</TableCell>
                    <TableCell className="max-w-[280px] truncate text-[11px] text-muted-foreground" title={r.user_agent ?? ""}>
                      {r.user_agent ?? "—"}
                    </TableCell>
                  </TableRow>
                ))}
                {!isFetching && (data?.length ?? 0) === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                      Žádné záznamy neodpovídají filtrům.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
